import { queryClient } from "../../../middlewares/cacheing/QueryClient.middleware";
import accountService from "../../../services/accounts/account.service";
import { accountActionTypes } from "../../actionTypes/accounts/account.actionTypes";
import { orderActionTypes } from "../../actionTypes/orders/order.actionTypes";
import { agentOrderActionTypes } from "../../actionTypes/agentOrder/agentOrder.actionTypes";
import { AGENT_ORDER_STORAGE_KEY } from "../../../middlewares/agentOrder/agentOrder.middleware";

export const login = (credentials) => async (dispatch) => {
  try {
    dispatch({ type: accountActionTypes.START_LOGIN });

    const response = await accountService.login(credentials);
    const token = response.data.payload;
    dispatch({
      type: accountActionTypes.LOGIN_SUCCESS,
      data: token,
    });
    // Save the token to local storage
    localStorage.setItem("jwtToken", token);
    return response.data;
  } catch (ex) {
    console.log("Login error: " + ex.message);
    const errorResponse = ex.response?.data;
    dispatch({ type: accountActionTypes.LOGIN_FAILURE, error: errorResponse });
    return errorResponse;
  }
};

export const registerBusiness = (userDetails) => async (dispatch) => {
  try {
    dispatch({ type: accountActionTypes.START_REGISTER_BUSINESS });

    const response = await accountService.registerBusiness(userDetails);
    // 0007: business accounts are created ONLY by an admin (no self-registration path). The server
    // returns the NEW account's token in payload, but we must NOT persist it — doing so would
    // overwrite the admin's own jwtToken and hijack their session into the new user. The reducer
    // (REGISTER_BUSINESS_SUCCESS -> setStateSuccess) does not use the token either.
    dispatch({ type: accountActionTypes.REGISTER_BUSINESS_SUCCESS });
    return response.data;
  } catch (ex) {
    console.log("Register BUSINESS error: " + ex.message);
    const errorResponse = ex.response?.data;
    dispatch({
      type: accountActionTypes.REGISTER_BUSINESS_FAILURE,
      error: errorResponse,
    });
    return errorResponse;
  }
};

export const logout = () => (dispatch) => {
  localStorage.removeItem("jwtToken");
  dispatch({ type: accountActionTypes.LOGOUT });
  dispatch({ type: orderActionTypes.CLEAR_ORDERS }); // Clear orders

  // 0017: clear the on-behalf selected customer so it never leaks into the next session/role.
  // Dispatch the clear first (resets in-memory state), then remove the persisted key LAST — the
  // persistence middleware re-writes the key on every dispatched action, so the removal must run
  // after all dispatches above.
  dispatch({ type: agentOrderActionTypes.CLEAR_SELECTED_CUSTOMER });

  // ✅ Invalidate orders cache to ensure fresh data on next login
  queryClient.invalidateQueries({ queryKey: ["business-orders"] });

  // 0015: drop the cached UserInfo query so a subsequent login refetches it fresh (the query is
  // keyed on the token, but remove it explicitly so no stale user data lingers in the RQ cache).
  queryClient.removeQueries({ queryKey: ["userInfo"] });

  localStorage.removeItem(AGENT_ORDER_STORAGE_KEY);
};

export const getUserInfo = (data) => async (dispatch) => {
  try {
    dispatch({ type: accountActionTypes.START_GET_USER_INFO });

    const response = await accountService.getUserInfo(data);
    dispatch({
      type: accountActionTypes.GET_USER_INFO_SUCCESS,
      data: response.data.payload,
    });
  } catch (ex) {
    console.log("Get user info error: " + ex.message);
    dispatch({
      type: accountActionTypes.GET_USER_INFO_FAILURE,
      error: ex.response?.data,
    });
  }
};

export const getUsersByBusinessCustomer = () => async (dispatch) => {
  try {
    dispatch({ type: accountActionTypes.START_GET_USERS_BY_BUSINESS });

    const response = await accountService.getUsersByBusinessCustomer();
    const users = response.data.payload;

    dispatch({
      type: accountActionTypes.GET_USERS_BY_BUSINESS_SUCCESS,
      data: users,
    });

    return users; // Return users for further processing if needed
  } catch (ex) {
    console.log("Get users by business customer error: " + ex.message);
    const errorResponse = ex.response?.data;

    dispatch({
      type: accountActionTypes.GET_USERS_BY_BUSINESS_FAILURE,
      error: errorResponse,
    });

    return errorResponse; // Return error for further processing if needed
  }
};

export const deleteAccount = (email) => async (dispatch) => {
  try {
    dispatch({ type: accountActionTypes.START_DELETE_ACCOUNT });

    const response = await accountService.deleteAccount(email);

    dispatch({
      type: accountActionTypes.DELETE_ACCOUNT_SUCCESS,
      data: email,
    });

    return response.data; // Return success response
  } catch (ex) {
    console.log("Delete account error: " + ex.message);
    const errorResponse = ex.response?.data;

    dispatch({
      type: accountActionTypes.DELETE_ACCOUNT_FAILURE,
      error: errorResponse,
    });

    return errorResponse; // Return error response
  }
};

const accountActions = {
  login,
  registerBusiness,
  logout,
  getUserInfo,
  getUsersByBusinessCustomer,
  deleteAccount,
};

export default accountActions;
